<template>
  <div class="ma-4">
      <v-snackbar
        v-model="msg"
        top
        :color="snackbarColor"
        :timeout="3000"
        elevation="4"
        class="rounded-lg"
      >
        <div class="d-flex align-center">
          <v-icon class="mr-2">{{
            snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
          }}</v-icon>
          {{ text }}
        </div>
        <template v-slot:action="{ attrs }">
          <v-btn icon small v-bind="attrs" @click="msg = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>

      <v-card class="rounded-lg">
        <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
          <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
            <v-icon>navigate_before</v-icon>
          </v-btn>

          <span class="text-h6 font-weight-medium grey--text text--darken-3">
            Time Off Requests
          </span>

          <v-spacer></v-spacer>
          <report-list-actions
            :reportOptions="reportOptions"
            :downloadFilename="reportListFilename"
          ></report-list-actions>

        </v-app-bar>

        <v-divider></v-divider>

        <v-card-text class="pa-6 grey lighten-5">
          <v-row dense>
            <v-col cols="12" md="2">
              <v-menu
                ref="calendarMenu"
                v-model="calendarMenu"
                :close-on-content-click="false"
                :return-value.sync="date"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="reportFilters.startDate"
                    label="Start Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    hide-details
                    class="rounded-lg"
                    background-color="white"
                    v-bind="attrs"
                    v-on="on"
                    :rules='requiredField'
                    clearable
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="reportFilters.startDate"
                  no-title
                  scrollable
                  color="primary"
                  class="rounded-lg"
                  :rules='requiredField'
                >
                  <v-spacer></v-spacer>
                  <v-btn
                    text
                    color="primary"
                    @click="calendarMenu = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    text
                    color="primary"
                    @click="$refs.calendarMenu.save(date)"
                  >
                    OK
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" md="2">
              <v-menu
                ref="calendar2Menu"
                v-model="calendar2Menu"
                :close-on-content-click="false"
                :return-value.sync="date"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="reportFilters.endDate"
                    label="End Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    hide-details
                    class="rounded-lg"
                    background-color="white"
                    v-bind="attrs"
                    v-on="on"
                    :rules='requiredField'
                    clearable
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="reportFilters.endDate"
                  no-title
                  scrollable
                  color="primary"
                  class="rounded-lg"
                  :rules='requiredField'
                >
                  <v-spacer></v-spacer>
                  <v-btn
                    text
                    color="primary"
                    @click="calendar2Menu = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    text
                    color="primary"
                    @click="$refs.calendar2Menu.save(date)"
                  >
                    OK
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12" md="2">
              <v-select
                label="Staff Status"
                :items="staffStatuses"
                v-model="reportFilters.staffStatus"
                outlined
                dense
                hide-details
                class="rounded-lg"
                background-color="white"
                clearable
              ></v-select>
            </v-col>

            <v-col cols="12" md="2">
              <v-select
                label="Select Staff"
                :items="filteredStaff"
                item-text="Name"
                item-value="_id"
                v-model="reportFilters.staffID"
                outlined
                dense
                hide-details
                class="rounded-lg"
                background-color="white"
                clearable
              ></v-select>
            </v-col>

            <v-col cols="12" md="2">
              <v-select
                label="Select Program"
                :items="programs"
                item-text="Name"
                item-value="_id"
                v-model="reportFilters.Program"
                outlined
                dense
                hide-details
                class="rounded-lg"
                background-color="white"
                clearable
              ></v-select>
            </v-col>

            <v-col cols="12" md="2" class="d-flex align-center">
              <v-btn
                color="primary"
                block
                height="40"
                class="rounded-lg"
                :loading="isLoading"
                elevation="1"
                @click="getTimeOffRequests()"
              >
                <v-icon left>mdi-magnify</v-icon>
                Search
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Data Table -->
        <v-card-text class="pa-6">
          <v-data-table
            :headers="headers"
            :items="timeOffs"
            :loading="isLoading"
            loading-text="Loading Time Off Requests"
            :search="reportFilters.search"
            height="550"
            class="elevation-1 rounded-lg"
            :footer-props="{
              'items-per-page-options': [5, 10, 20, 50],
              showFirstLastPage: true,
              'items-per-page-text': 'Rows per page:',
              class: 'rounded-lg',
            }"
          >
            <template v-slot:top>
              <v-toolbar flat color="transparent" class="px-4 py-2">
                <v-text-field
                  v-model="reportFilters.search"
                  clearable
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-magnify"
                  label="Search time off requests..."
                  class="rounded-lg"
                  background-color="white"
                  style="max-width: 400px;"
                ></v-text-field>
                <v-spacer></v-spacer>
              </v-toolbar>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-icon small class="mr-2" @click="viewTimeOff(item)">
                mdi-eye
              </v-icon>

              <v-icon small @click="deleteTimeOff(item)">
                mdi-delete
              </v-icon>
            </template>

            <template v-slot:no-data>
              <v-btn color="#2b3990" class="white--text" @click="getTimeOffRequests()">Reset</v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Print Section -->
      <div id="print">
        <report-header>
          <h2>Time Off Request</h2>
        </report-header>
        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="timeOffs"
              class="elevation-1"
              :loading="isLoading"
              loading-text="Loading Time Off Requests"
              :items-per-page="-1"
              @update:options="getTimeOffRequests"
            >
            </v-data-table>
          </v-col>
        </v-row>
      </div>

      <Foot/>
  </div>
</template>

<script>
import axios from 'axios'
import Foot from '../../components/Footer'
import DateTimeUtil from "../../../utils/date-time.util";
import {
STATUS_ARRAY,
STATUSES as UserStatus
} from "../../enums/UserStatusEnum";
import { STATUSES as StaffStatus } from "../../enums/StaffStatusEnum";
import ReportFilters from '@/mixins/reportFilters.js';
import ReportHeader from '@/components/Report/ReportHeader.vue';
import ReportListActions from '@/components/Report/ReportListActions.vue'

const url = require("../../client_config/config").url;

export default {
  mixins: [
    ReportFilters,
  ],
  components: {
      ReportHeader,
      ReportListActions,
      Foot,
  },
  data: function () {
      return {
          headers: [
              {
                  text: 'Staff',
                  align: 'start',
                  value: 'StaffID.Name',
              },
              {text: "Start Date", value: "StartDate"},
              {text: "End Date", value: "EndDate"},
              {text: "Date Requested", value: "CreatedDate"},
              {text: "Reason", value: "Reason"},
              {text: "Status", value: "Approved"},
              {text: "Signed By", value: "SignedBy"},
              {text: "Actions", value: "actions", sortable: false},
          ],
          msg: false,
          menu: false,
          text: "",
          timeOffs: [],
          programs: [],
          managers: [],
          programID:"",
          staff: "",
          timeOffID: "",
          isLoading: true,
          userState: localStorage.getItem('userState'),
          reportListFilename: 'Time Off Request Report',
          options: {},
          staffStatuses: STATUS_ARRAY,
          users: [],
          reportFilters:  {
              startDate: '',
              endDate: '',
              staffStatus: '',
              staffID: '',
              Program: '',
              search: ''
          },
          reportFilterName: 'ReportFilters_TimeOffRequest',
          requiredField: [(v) => !!v || "This is required"],
          snackbarColor: "success",
      };
  },
  computed: {
    filteredStaff() {
      const staffs = this.users;
      const staffStatus = this.reportFilters.staffStatus;

      if (staffStatus === UserStatus.INACTIVE) {
        return staffs.filter(
          (staff) => staff.Status == StaffStatus.TERMINATED
        );
      }

      if (staffStatus === UserStatus.ACTIVE) {
        return staffs.filter(
          (staff) =>
            staff.Status == StaffStatus.FULL_TIME ||
            staff.Status == StaffStatus.PART_TIME
        );
      }

      return staffs;
    },

    queryParams() {
        let {page,itemsPerPage} = this.options;      
        const companyIds = localStorage.getItem('userCompanies');

        return {            
          startDate: this.reportFilters.startDate,
          endDate: this.reportFilters.endDate,
          staffStatus: this.reportFilters.staffStatus,
          staffID: this.reportFilters.staffID,
          programID: this.reportFilters.Program,
          Company: JSON.parse(companyIds),
          page: page,
          limit: itemsPerPage,
        };

    },

    reportOptions() {
        return { 
            query: this.queryParams,
            endpoints: `timeoff/print-timeoffreport`,
            dynamic: true
        }
    }

  },

  watch: {
    reportFilters: {
      handler(newVal) {
        this.setReportFilters(this.reportFilterName, newVal);
      },
      deep: true
    }
  },

  created() {
      this.getTimeOffRequests();
      this.getPrograms();
      this.getManagers();
      this.getEmployees();
      this.reportFilters = this.getReportFilters(this.reportFilterName) ?? this.reportFilters;
  },

  methods: {
      initialize() {
          this.timeOffs = [];
      },
      async getManagers() {
          let endpoint = '';
          const companyIds = localStorage.getItem('userCompanies');

          endpoint = `${url}/users`;

          await axios.get(endpoint, {
              params: {
                  Company: JSON.parse(companyIds)
              }
          }).then(res => {
              this.managers = res.data.users;

          });
      },

      viewTimeOff(item) {
        let staffId = item.StaffID._id;
          this.$router.push({
              path: `/timeOff/${staffId}/update-time-off-request/${item._id}`,
              query: { showNav: 'false' }
          });
      },

      printTable() {
          const printOptions = {
              page: 1,
              itemsPerPage: -1,
          };
          
          this.getTimeOffRequests(printOptions);
          window.print();
      },

      async getPrograms() {
          const companyIds = localStorage.getItem('userCompanies');

          await axios.get(`${url}/units`, {
              params: {
                  Company: JSON.parse(companyIds)
              }
          })
              .then((res) => {
                  this.programs = res.data.units;
              });
      },

      async getEmployees () {
          let endpoint = '';
          const companyIds = localStorage.getItem('userCompanies');

          endpoint = `${url}/users/state/${this.state}`;

          await axios.get(endpoint, {
              params: {
              Company: JSON.parse(companyIds)
              }
          }).then(res => {
              this.users = res.data.users;
          });
      },
      deleteTimeOff(item) {
          confirm("Are you sure you want to delete this time off request?") &&
          this.timeOffDelete(item);
      },

      timeOffDelete(item) {
          const id = item._id;
          let req_body = {
              Url: window.location.href,
              StaffID: item.StaffID._id || item.StaffID,
          };
          axios.delete(`${url}/timeOff/${id}`, { params: req_body })
              .then((response) => {
                  this.getTimeOffRequests();
                  if (response.status === 400) {
                      response.json().then(function (data) {
                          return data.msg;
                      });
                  }
                  this.msg = true;
                  this.text = "The time off request has been deleted successfully.";
              });
      },

      close() {
          this.clearData()
      },

     getTimeOffRequests(options = {}) {
          this.isLoading = true;

          const { page = 1, itemsPerPage = 10 } = options;

          const params = {
              startDate: this.reportFilters.startDate,
              endDate: this.reportFilters.endDate,
              staffStatus: this.reportFilters.staffStatus,
              staffID: this.reportFilters.staffID,
              programID: this.reportFilters.Program,
              page: page,
              limit: itemsPerPage,
          };

          axios.get(`${url}/timeOff/state/${this.userState}`, { params })
              .then(res => {
                  if (!res.data || !Array.isArray(res.data.timeOffs)) {
                  console.error("Unexpected response format:", res.data);
                  this.timeOffs = [];
                  return;
              }
              this.timeOffs = res.data.timeOffs.map(item => {
                  const signedBy = item.SignedUser
                  ? `${item.SignedUser} ${DateTimeUtil.toLocalDateTime(item.SignedDate)}`
                  : item.SignedBy || 'Not yet signed';

                  return {
                  ...item,
                  SignedBy: signedBy
                  };
              });
          }).catch(error => {
              console.error("Error fetching time off requests:", error);
              this.msg = true;
              this.text = "Failed to fetch time off requests. Please try again.";
          })
          .finally(() => {
              this.isLoading = false;
          });
      },
      
      clearData() {
          this.newStartDate = "";
          this.newEndDate = "";
          this.newReason = "";
          this.newManager = "";
          this.newProgram = "";
          this.newApproved = "";
          this.newDeniedReason = "";
          this.newRequestDate = "";
          this.newSignedBy = "";
          this.approved = false;
          this.denied = false;
      },
  },

};
</script>
<style scoped>
.v-data-table {
border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
background-color: #f5f5f5 !important;
}

.v-btn {
text-transform: none;
letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
margin-top: 8px !important;
}

.rounded-lg {
border-radius: 8px !important;
}

.rounded-t-lg {
border-top-left-radius: 8px !important;
border-top-right-radius: 8px !important;
}

@media only screen and (min-width: 768px) {
.mobile {
  display: none;
}
}

@media only screen and (max-width: 600px) {
.desktop {
  display: none;
}
}

@media print {
#print {
  display: block;
  border: none;
  width: 1200px;
  padding-right: 300px;
  margin-left: -200px;
}

nav.v-navigation-drawer, header.v-toolbar, button.v-btn, div.v-card {
  display: none;
  border: none;
}

@page {
  margin: 0;
}

body {
  margin: 1.6cm;
}
}
</style>